import React, { createContext, useReducer, useContext, useEffect, useState } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import firebase from '../Firebase';

if (!getApps().length) {
  initializeApp(firebase);
}

const initialState = {
  messages: [],
  currentConversation: null,
  conversations: [],
  otherUserDisplay: null,
  community: null,
  userDisplayProfile: null,
  lastReadMessageId: null,
  typingUsers: {},
  typingBots: {},
  userInterests: {}, // Track user interest states by userId
  userInterestTimestamps: {}, // Track when user interests were last updated
};
 
const messengerReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MESSAGES':
      console.log('[messenger load debug] 🔄 SET_MESSAGES called, replacing', state.messages.length, 'messages with', action.payload.length, 'messages');
      console.log('[messenger load debug] 🔄 New messages:', action.payload.map(msg => ({
        messageId: msg.messageId || msg._id,
        text: msg.text?.substring(0, 30) + '...'
      })));
      return { ...state, messages: action.payload };
    case 'ADD_MESSAGE':
      console.log('[messenger load debug] 🔄 MessengerContext ADD_MESSAGE received:', {
        messageId: action.payload.messageId,
        text: action.payload.text?.substring(0, 50) + (action.payload.text?.length > 50 ? '...' : ''),
        sender: action.payload.sender,
        _id: action.payload._id,
        tempId: action.payload.tempId
      });
      
      console.log('[messenger load debug] 🔄 Current state messages:', state.messages.map(msg => ({
        messageId: msg.messageId,
        _id: msg._id,
        tempId: msg.tempId,
        text: msg.text?.substring(0, 30) + '...'
      })));
      
      // Prevent duplicate messages by checking messageId
      console.log('[messenger load debug] 🔍 Checking for duplicates. Incoming message ID:', action.payload.messageId);
      console.log('[messenger load debug] 🔍 Existing message IDs:', state.messages.map(msg => msg.messageId || msg._id));
      
      const existingMessage = state.messages.find(msg => {
        const idMatch = action.payload._id && msg._id === action.payload._id;
        const messageIdMatch = action.payload.messageId && msg.messageId === action.payload.messageId;
        const tempIdMatch = action.payload.tempId && msg.tempId === action.payload.tempId;
        
        const match = idMatch || messageIdMatch || tempIdMatch;
        
        if (match) {
          console.log('[messenger load debug] 🔍 Found duplicate match:', {
            matchType: idMatch ? '_id' : messageIdMatch ? 'messageId' : 'tempId',
            existingMsg: { _id: msg._id, messageId: msg.messageId, tempId: msg.tempId },
            incomingMsg: { _id: action.payload._id, messageId: action.payload.messageId, tempId: action.payload.tempId }
          });
        }
        
        return match;
      });
      
      if (existingMessage) {
        console.log('[messenger load debug] 🚫 Duplicate message detected, skipping:', {
          incomingMessageId: action.payload.messageId,
          existingMessageId: existingMessage.messageId,
          existingMessage: existingMessage
        });
        return state;
      }
      
      console.log('[messenger load debug] 🔄 Adding message to state, current message count:', state.messages.length);
      const newState = { ...state, messages: [...state.messages, action.payload] };
      console.log('[messenger load debug] 🔄 New message count:', newState.messages.length);
      return newState;
    case 'ADD_OPTIMISTIC_MESSAGE':
      return { ...state, messages: [...state.messages, { ...action.payload, status: 'pending' }] };
    case 'UPDATE_MESSAGE_STATUS':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.tempId === action.payload.tempId
            ? { ...msg, ...action.payload.updates }
            : msg
        )
      };
    case 'REPLACE_OPTIMISTIC_MESSAGE':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.tempId === action.payload.tempId
            ? { 
                ...action.payload.serverMessage, 
                messageId: action.payload.serverMessage._id,
                status: 'sent' 
              }
            : msg
        )
      };
    case 'SET_CURRENT_CONVERSATION':
      return { ...state, currentConversation: action.payload };
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };
    case 'ADD_CONVERSATION':
      return { ...state, conversations: [action.payload, ...state.conversations] };
    case 'SET_OTHER_USER_DISPLAY':
      return { ...state, otherUserDisplay: action.payload };
    case 'SET_COMMUNITY_DISPLAY':
      return { ...state, community: action.payload };
    case 'SET_USER_DISPLAY':
      return { ...state, userDisplayProfile: action.payload };
    case 'SET_LAST_READ_MESSAGE_ID':
      return { ...state, lastReadMessageId: action.payload };
    case 'UPDATE_LAST_READ_MESSAGE_ID':
      console.log('UPDATE_LAST_READ_MESSAGE_ID action fired', action.payload);
      if (!state.currentConversation || !state.currentConversation.lastRead) return state;
      const updatedLastRead = state.currentConversation.lastRead.map(item =>
        item.user === action.payload.userId
          ? { ...item, message: action.payload.messageId }
          : item
      );
      
      // Also update the conversations list to clear unread status
      const updatedConversations = state.conversations.map(conversation =>
        conversation._id === state.currentConversation._id
          ? { ...conversation, unReadMessages: false }
          : conversation
      );
      
      return {
        ...state,
        currentConversation: {
          ...state.currentConversation,
          lastRead: updatedLastRead,
          unReadMessages: false,
        },
        conversations: updatedConversations,
      };
    case 'UPDATE_CONVERSATION_UNREAD':
      return {
        ...state,
        conversations: state.conversations.map(conversation =>
          conversation._id === action.payload
            ? { ...conversation, unReadMessages: true }
            : conversation
        )
      };
    case 'SET_USER_TYPING':
      console.log("🔄 Reducer: SET_USER_TYPING", action.payload);
      const newTypingState = {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.conversationId]: {
            ...state.typingUsers[action.payload.conversationId],
            [action.payload.userId]: action.payload.timestamp
          }
        }
      };
      console.log("🔄 New typing state after SET_USER_TYPING:", newTypingState.typingUsers);
      return newTypingState;
    case 'SET_USER_STOPPED_TYPING':
      console.log("🔄 Reducer: SET_USER_STOPPED_TYPING", action.payload);
      console.log("🔄 Current typing state before removal:", state.typingUsers);
      const { [action.payload.userId]: removed, ...remainingUsers } = state.typingUsers[action.payload.conversationId] || {};
      const newStoppedState = {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.conversationId]: remainingUsers
        }
      };
      console.log("🔄 New typing state after SET_USER_STOPPED_TYPING:", newStoppedState.typingUsers);
      return newStoppedState;
    case 'SET_BOT_TYPING':
      console.log("🤖 [MessengerContext] Processing SET_BOT_TYPING:", action.payload);
      const newBotTypingState = {
        ...state,
        typingBots: {
          ...state.typingBots,
          [action.payload.conversationId]: {
            ...state.typingBots[action.payload.conversationId],
            [action.payload.botId]: action.payload.timestamp
          }
        }
      };
      console.log("🤖 [MessengerContext] New bot typing state:", newBotTypingState.typingBots);
      return newBotTypingState;
    case 'SET_BOT_STOPPED_TYPING':
      const { [action.payload.botId]: removedBot, ...remainingBots } = state.typingBots[action.payload.conversationId] || {};
      return {
        ...state,
        typingBots: {
          ...state.typingBots,
          [action.payload.conversationId]: remainingBots
        }
      };
    case 'SET_USER_INTEREST':
      console.log('[profileinterest debug] 🎯 SET_USER_INTEREST action:', action.payload);
      console.log('[profileinterest debug] 🎯 Current userInterests before update:', state.userInterests);
      const currentTime = Date.now();
      const newInterestState = {
        ...state,
        userInterests: {
          ...state.userInterests,
          [action.payload.userId]: action.payload.isInterested ? 'interested' : 'rejected'
        },
        userInterestTimestamps: {
          ...state.userInterestTimestamps,
          [action.payload.userId]: currentTime
        }
      };
      console.log('[profileinterest debug] ✅ Updated userInterests state after SET_USER_INTEREST:', newInterestState.userInterests);
      console.log('[profileinterest debug] ✅ Set timestamp for user', action.payload.userId, ':', currentTime);
      return newInterestState;
    case 'SET_BULK_USER_INTERESTS':
      console.log('[profileinterest debug] SET_BULK_USER_INTERESTS action payload:', action.payload);
      console.log('[profileinterest debug] Current userInterests before bulk update:', state.userInterests);
      console.log('[profileinterest debug] Current userInterestTimestamps:', state.userInterestTimestamps);
      const newUserInterests = { ...state.userInterests };
      const bulkUpdateTime = Date.now();
      const recentInteractionThreshold = 5000; // 5 seconds - don't overwrite recent user interactions
      
      console.log('[profileinterest debug] Starting bulk update at time:', bulkUpdateTime, 'threshold:', recentInteractionThreshold);
      
      // Add interested users - but don't overwrite existing interests or recent interactions
      if (action.payload.userInterestedIn) {
        console.log('[profileinterest debug] Processing userInterestedIn array:', action.payload.userInterestedIn);
        action.payload.userInterestedIn.forEach(userId => {
          const userTimestamp = state.userInterestTimestamps[userId];
          const isRecentInteraction = userTimestamp && (bulkUpdateTime - userTimestamp < recentInteractionThreshold);
          console.log('[profileinterest debug] Processing interested userId:', userId, {
            currentValue: newUserInterests[userId],
            timestamp: userTimestamp, 
            timeSinceInteraction: userTimestamp ? bulkUpdateTime - userTimestamp : 'no timestamp',
            isRecent: isRecentInteraction,
            hasTimestamp: !!userTimestamp,
            willUpdate: newUserInterests[userId] === undefined && (!userTimestamp || !isRecentInteraction)
          });
          
          if (newUserInterests[userId] === undefined && (!userTimestamp || !isRecentInteraction)) {
            newUserInterests[userId] = 'interested';
            console.log('[profileinterest debug] ✅ Set user', userId, 'as INTERESTED');
          } else {
            const reason = newUserInterests[userId] !== undefined ? 'already has interest (' + newUserInterests[userId] + ')' : 'recent interaction';
            console.log('[profileinterest debug] ⏭️ Skipping interested user', userId, 'reason:', reason);
          }
        });
      } else {
        console.log('[profileinterest debug] No userInterestedIn in payload');
      }
      
      // Add rejected users - but don't overwrite existing interests or recent interactions
      if (action.payload.userRejected) {
        console.log('[profileinterest debug] Processing userRejected array:', action.payload.userRejected);
        action.payload.userRejected.forEach(userId => {
          const userTimestamp = state.userInterestTimestamps[userId];
          const isRecentInteraction = userTimestamp && (bulkUpdateTime - userTimestamp < recentInteractionThreshold);
          console.log('[profileinterest debug] Processing rejected userId:', userId, {
            currentValue: newUserInterests[userId],
            timestamp: userTimestamp,
            timeSinceInteraction: userTimestamp ? bulkUpdateTime - userTimestamp : 'no timestamp', 
            isRecent: isRecentInteraction,
            hasTimestamp: !!userTimestamp,
            willUpdate: newUserInterests[userId] === undefined && (!userTimestamp || !isRecentInteraction)
          });
          
          if (newUserInterests[userId] === undefined && (!userTimestamp || !isRecentInteraction)) {
            newUserInterests[userId] = 'rejected';
            console.log('[profileinterest debug] ❌ Set user', userId, 'as REJECTED');
          } else {
            const reason = newUserInterests[userId] !== undefined ? 'already has interest (' + newUserInterests[userId] + ')' : 'recent interaction';
            console.log('[profileinterest debug] ⏭️ Skipping rejected user', userId, 'reason:', reason);
          }
        });
      } else {
        console.log('[profileinterest debug] No userRejected in payload');
      }
      
      console.log('[profileinterest debug] 🎯 FINAL bulk userInterests state:', newUserInterests);
      console.log('[profileinterest debug] 🎯 Changes made:', Object.keys(newUserInterests).filter(key => !state.userInterests[key]));
      return {
        ...state,
        userInterests: newUserInterests
      };
    case 'CLEAR_ALL_DATA':
      return {
        messages: [],
        currentConversation: null,
        conversations: [],
        otherUserDisplay: null,
        community: null,
        userDisplayProfile: null,
        lastReadMessageId: null,
        typingUsers: {},
        typingBots: {},
        userInterests: {},
        userInterestTimestamps: {},
      };
    case 'GROUP_BOT_INVITED':
      // Mark the current conversation as a group conversation when bot is invited
      if (!state.currentConversation || state.currentConversation._id !== action.payload.conversationId) {
        return state;
      }
      return {
        ...state,
        currentConversation: { ...state.currentConversation, isGroup: true },
        conversations: state.conversations.map(c =>
          c._id === action.payload.conversationId ? { ...c, isGroup: true } : c
        )
      };
    case 'GROUP_MEMBER_JOINED':
      // Add new member to current conversation's members array
      return {
        ...state,
        conversations: state.conversations.map(c =>
          c._id === action.payload.conversationId
            ? { ...c, members: [...new Set([...(c.members || []), action.payload.userId])] }
            : c
        ),
        currentConversation: state.currentConversation?._id === action.payload.conversationId
          ? { ...state.currentConversation, members: [...new Set([...(state.currentConversation.members || []), action.payload.userId])] }
          : state.currentConversation
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export const MessengerContext = createContext();

export const MessengerContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(messengerReducer, initialState);
  const { user } = useAuthContext();
  const [token, setToken] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        const idToken = await user.getIdToken();
        setToken(idToken);
      } else {
        // Clear all messenger data when user logs out
        dispatch({ type: 'CLEAR_ALL_DATA' });
        setToken(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchWithToken = async (url, options) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return response.json();
  };

  useEffect(() => {
    if (state.currentConversation && state.currentConversation.lastRead) {
      const newLastReadMessageId = state.currentConversation.lastRead.find(lr => lr.user === user.userId)?.message;
      dispatch({ type: 'SET_LAST_READ_MESSAGE_ID', payload: newLastReadMessageId });
      console.log('lastReadMessageId context', newLastReadMessageId)
    }
  }, [state.currentConversation, user]);

  useEffect(() => {
    if (!user || !state.currentConversation || !token) return;
    const getMessages = async () => {
      if (state.currentConversation?._id) {
        try {
          console.log('[messenger load debug] 🔄 Fetching messages from DB for conversation:', state.currentConversation._id);
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/messages/${state.currentConversation._id}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
          });
          const json = await response.json();
          console.log('[messenger load debug] 🔄 About to SET_MESSAGES with', json.length, 'messages from DB');
          dispatch({ type: 'SET_MESSAGES', payload: json });
          
          // Check if this is a bot conversation with no messages - trigger welcome
          const isBotConversation = state.currentConversation.communityBot || 
                                   state.currentConversation.members.includes(state.currentConversation.communityId);
          
          // Check if welcome has already been sent by looking for the first welcome message
          const hasWelcomeMessage = json.some(message => 
            message.text && message.text.includes('Hello and welcome to')
          );
          
          if (isBotConversation && !hasWelcomeMessage) {
            const urlParams = new URLSearchParams(window.location.search);
            const fromSource = urlParams.get('from');
            console.log(`🤖 [Frontend] Bot conversation without welcome message detected - triggering welcome (from: ${fromSource || 'unknown'})`);
            
            // Wait a moment to ensure socket connection is fully established
            await new Promise(resolve => setTimeout(resolve, 500));
            console.log('🤖 [Frontend] Socket connection delay completed, triggering welcome');
            
            // Trigger welcome message
            try {
              const welcomeResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/messages/triggerWelcome`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  conversationId: state.currentConversation._id,
                  userId: user.userId,
                  communityId: state.currentConversation.communityId
                })
              });
              
              const welcomeResult = await welcomeResponse.json();
              console.log('🤖 [Frontend] Welcome trigger result:', welcomeResult);
              
              // Removed automatic refresh - let socket messages handle updates naturally
            } catch (welcomeError) {
              console.error('🤖 [Frontend] Error triggering welcome message:', welcomeError);
            }
          }
        } catch (error) {
          console.error('Failed to fetch messages:', error);
        }
      }
    };
    getMessages();
  }, [state.currentConversation?._id, user, token]); // Only run when conversation ID changes, not when conversation object changes

  const refreshConversations = async () => {
    console.log('[HOMEPAGE MESSAGE CHECK] 🔄 refreshConversations called', { hasUser: !!user, hasToken: !!token, userId: user?.userId });
    if (!user || !token) {
      console.log('[HOMEPAGE MESSAGE CHECK] ⚠️ Skipping - missing user or token');
      return;
    }
    try {
      console.log('[HOMEPAGE MESSAGE CHECK] 📡 Fetching conversations from API...');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/conversations/getConversationsWithUnreadMessages/${user.userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      const json = await response.json();
      console.log('[HOMEPAGE MESSAGE CHECK] ✅ API response received:', { 
        conversationCount: json.length, 
        conversations: json.map(conv => ({
          id: conv._id,
          unReadMessages: conv.unReadMessages,
          lastMessage: conv.lastMessage?.text?.substring(0, 50),
          communityName: conv.communityName || 'Unknown'
        }))
      });
      dispatch({ type: 'SET_CONVERSATIONS', payload: json });
      console.log('[HOMEPAGE MESSAGE CHECK] 🔄 Conversations dispatched to state');
    } catch (error) {
      console.error('[HOMEPAGE MESSAGE CHECK] ❌ Failed to fetch conversations:', error);
    }
  };

  useEffect(() => {
    console.log('[HOMEPAGE MESSAGE CHECK] 🎆 MessengerContext useEffect triggered - calling refreshConversations');
    refreshConversations();
  }, [user, token]);

  useEffect(() => {
    if (!user || !token) return;
    const getUsers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/getBasicProfileToDisplay`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userId: user.userId }),
        });
        const json = await response.json();
        dispatch({ type: 'SET_USER_DISPLAY', payload: json });
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    getUsers();
    // Reload it when their photos change (first upload in onboarding, or
    // choosing the automatic avatar) — otherwise their own picture in the
    // chat stays blank until the page is refreshed.
    window.addEventListener('ic:pics-updated', getUsers);
    return () => window.removeEventListener('ic:pics-updated', getUsers);
  }, [user, token]);
useEffect(() => {
  if (!user || !state.currentConversation || !token) {
    console.log('[MessengerContext] Skipping getCommunityAndOtherUser: missing user, currentConversation, or token', {
      user,
      currentConversation: state.currentConversation,
      token
    });
    return;
  }

  // Community and other-user are independent lookups (neither's result
  // feeds the other's request), but were previously awaited one after
  // the other — doubling the wait before the header/profile could
  // render on every conversation switch. Fired together instead.
  const getCommunityAndOtherUser = async () => {
    try {
      console.log('[MessengerContext] Fetching community:', state.currentConversation.communityId);

      const members = state.currentConversation.members;
      console.log('[MessengerContext] Conversation members:', members);

      const otherUserId = members.find((m) => m !== user.userId && m !== state.currentConversation.communityId);
      console.log('[MessengerContext] Computed otherUserId:', otherUserId);

      const communityPromise = fetch(`${process.env.REACT_APP_API_BASE_URL}/communities/display/${state.currentConversation.communityId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      }).then((r) => r.json());

      const otherUserPromise = otherUserId
        ? fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfileToDisplay`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userId: otherUserId, communityId: state.currentConversation.communityId }),
        }).then((r) => r.json())
        : null;

      const [communityJson, userJson] = await Promise.all([communityPromise, otherUserPromise]);
      console.log('[MessengerContext] Community fetch response:', communityJson);
      dispatch({ type: 'SET_COMMUNITY_DISPLAY', payload: communityJson });

      if (otherUserId) {
        console.log('[MessengerContext] Other user profile response:', userJson);
        dispatch({ type: 'SET_OTHER_USER_DISPLAY', payload: userJson });
      } else {
        console.log('[MessengerContext] No otherUserId found - this is a community bot conversation, using community data');
        dispatch({ type: 'SET_OTHER_USER_DISPLAY', payload: communityJson });
      }
    } catch (error) {
      console.error('[MessengerContext] Failed to fetch community or user:', error);
    }
  };

  getCommunityAndOtherUser();
}, [state.currentConversation, user, token]);

  // useEffect(() => {
  //   if (!user || !state.currentConversation || !token) return;
  //   const getCommunityAndOtherUser = async () => {
  //     try {
  //       const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/communities/display/${state.currentConversation.communityId}`, {
  //         method: 'GET',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           'Authorization': `Bearer ${token}`
  //         },
  //       });
  //       const communityJson = await response.json();
  //       dispatch({ type: 'SET_COMMUNITY_DISPLAY', payload: communityJson });

  //       const otherUserId = state.currentConversation.members.find((m) => m !== user.userId && m !== state.currentConversation.communityId);
  //       if (otherUserId) {
  //         const requestBody = {
  //           userId: otherUserId,
  //           communityId: state.currentConversation.communityId,
  //         };
  //         const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfileToDisplay`, {
  //           method: 'POST',
  //           headers: {
  //             'Content-Type': 'application/json',
  //             'Authorization': `Bearer ${token}`
  //           },
  //           body: JSON.stringify(requestBody),
  //         });
  //         const userJson = await userResponse.json();
  //         dispatch({ type: 'SET_OTHER_USER_DISPLAY', payload: userJson });
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch community or user:', error);
  //     }
  //   };
  //   getCommunityAndOtherUser();
  // }, [state.currentConversation, user, token]);

  return (
    <MessengerContext.Provider value={{ state, dispatch, refreshConversations }}>
      {children}
    </MessengerContext.Provider>
  );
};

export const useMessenger = () => {
  const context = useContext(MessengerContext);
  if (context === undefined) {
    throw new Error('useMessenger must be used within a MessengerProvider');
  }
  return context;
};


// import React, { createContext, useReducer, useContext, useEffect, useState } from 'react';
// import { useAuthContext } from '../hooks/useAuthContext';
// import { initializeApp, getApps } from 'firebase/app';
// import { getAuth } from 'firebase/auth';
// import firebase from '../Firebase';

// if (!getApps().length) {
//   initializeApp(firebase);
// }

// const initialState = {
//   messages: [],
//   currentConversation: null,
//   otherUserDisplay: null,
//   community: null,
//   userDisplayProfile: null,
// };

// const messengerReducer = (state, action) => {
//   switch (action.type) {
//     case 'SET_MESSAGES':
//       return { ...state, messages: action.payload };
//     case 'ADD_MESSAGE':
//       return { ...state, messages: [...state.messages, action.payload] };
//     case 'SET_CURRENT_CONVERSATION':
//       return { ...state, currentConversation: action.payload };
//     case 'SET_CONVERSATIONS':
//       return { ...state, conversations: action.payload };
//     case 'ADD_CONVERSATION':
//       return { ...state, conversations: [action.payload, ...state.conversations] };
//     case 'SET_OTHER_USER_DISPLAY':
//       return { ...state, otherUserDisplay: action.payload };
//     case 'SET_COMMUNITY_DISPLAY':
//       return { ...state, community: action.payload };
//     case 'SET_USER_DISPLAY':
//       return { ...state, userDisplayProfile: action.payload };
//     case 'SET_LAST_READ_MESSAGE_ID':
//         return { ...state, lastReadMessageId: action.payload };
//     case 'UPDATE_LAST_READ_MESSAGE_ID':
//       console.log('UPDATE_LAST_READ_MESSAGE_ID action fired', action.payload);
//       const updatedLastRead = state.currentConversation.lastRead.map(item =>
//         item.user === action.payload.userId
//           ? { ...item, message: action.payload.messageId }
//           : item
//       );
//       return {
//         ...state,
//         currentConversation: {
//           ...state.currentConversation,
//           lastRead: updatedLastRead,
//         },
//       };
//     case 'UPDATE_CONVERSATION_UNREAD':
//       return {
//         ...state,
//         conversations: state.conversations.map(conversation => 
//           conversation._id === action.payload
//             ? { ...conversation, unReadMessages: true }
//             : conversation
//         )
//       };
//     default:
//       throw new Error(`Unhandled action type: ${action.type}`);
//   }
// };

// export const MessengerContext = createContext();



// export const MessengerContextProvider = ({ children }) => {
//   console.log('Rendering MessengerContextProvider');

//   const [state, dispatch] = useReducer(messengerReducer, initialState);
//   const { user } = useAuthContext();
//   const [token, setToken] = useState(null);
//   const auth = getAuth();


//   useEffect(() => {
//     const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
//       if (user) {
//         const idToken = await user.getIdToken(true);
//         setToken(idToken);
//       }
//     });
  
//     // Cleanup subscription on unmount
//     return () => unsubscribe();
//   }, []);

//   const fetchWithToken = async (url, options) => {
//     const response = await fetch(url, {
//       ...options,
//       headers: {
//         ...options.headers,
//         Authorization: `Bearer ${token}`,
//       },
//     });
//     if (!response.ok) {
//       throw new Error('Network response was not ok');
//     }
//     return response.json();
//   };

//   useEffect(() => {
//     if (state.currentConversation && state.currentConversation.lastRead) {
//       const newLastReadMessageId = state.currentConversation.lastRead.find(lr => lr.user === user.userId)?.message;
//       dispatch({ type: 'SET_LAST_READ_MESSAGE_ID', payload: newLastReadMessageId });
//       console.log('lastReadMessageId context', newLastReadMessageId)
//     }
//   }, [state.currentConversation, user]);

//   useEffect(() => {
//   if (!user || !state.currentConversation || !token) return;
//   const getMessages = async () => {
//     if (state.currentConversation?._id) {
//       try {
//         const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/messages/${state.currentConversation._id}`, {
//           method: 'GET',
//           headers: {
//             'Content-Type': 'application/json',
//             'Authorization': `Bearer ${token}`
//           },
//         });
//         const json = await response.json();
//         dispatch({ type: 'SET_MESSAGES', payload: json });
//       } catch (error) {
//         console.error('Failed to fetch messages:', error);
//       }
//     }
//   };
//   getMessages();
// }, [state.currentConversation, user, token]);

// useEffect(() => {
//   if (!user || !token) return;
//   const getConversations = async () => {
//     try {
//       const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/conversations/getConversationsWithUnreadMessages/${user.userId}`, {
//         method: 'GET',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${token}`
//         },
//       });
//       const json = await response.json();
//       dispatch({ type: 'SET_CONVERSATIONS', payload: json });
//     } catch (error) {
//       console.error('Failed to fetch conversations:', error);
//     }
//   };
//   getConversations();
// }, [state.currentConversation, user, token]);

// useEffect(() => {
//   if (!user || !token) return;
//   const getUsers = async () => {
//     try {
//       const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/getBasicProfileToDisplay`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${token}`
//         },
//         body: JSON.stringify({ userId: user.userId }),
//       });
//       const json = await response.json();
//       dispatch({ type: 'SET_USER_DISPLAY', payload: json });
//     } catch (error) {
//       console.error('Failed to fetch user:', error);
//     }
//   };
//   getUsers();
// }, [user, token]);

// useEffect(() => {
//   if (!user || !state.currentConversation || !token) return;
//   const getCommunityAndOtherUser = async () => {
//     try {
//       const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/communities/display/${state.currentConversation.communityId}`, {
//         method: 'GET',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${token}`
//         },
//       });
//       const communityJson = await response.json();
//       dispatch({ type: 'SET_COMMUNITY_DISPLAY', payload: communityJson });

//       const otherUserId = state.currentConversation.members.find((m) => m !== user.userId && m !== state.currentConversation.communityId);
//       if (otherUserId) {
//         const requestBody = {
//           userId: otherUserId,
//           communityId: state.currentConversation.communityId,
//         };
//         const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfileToDisplay`, {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//             'Authorization': `Bearer ${token}`
//           },
//           body: JSON.stringify(requestBody),
//         });
//         const userJson = await userResponse.json();
//         dispatch({ type: 'SET_OTHER_USER_DISPLAY', payload: userJson });
//       }
//     } catch (error) {
//       console.error('Failed to fetch community or user:', error);
//     }
//   };
//   getCommunityAndOtherUser();
// }, [state.currentConversation, user, token]);

  

//   return (
//     <MessengerContext.Provider value={{ state, dispatch }}>
//       {children}
//     </MessengerContext.Provider>
//   );
// };

// export const useMessenger = () => {
//   const context = useContext(MessengerContext);
//   if (context === undefined) {
//     throw new Error('useMessenger must be used within a MessengerProvider');
//   }
//   return context;
// };